import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalBody, Button } from "reactstrap";
import ImageAddIcon from "../../common/Components/Icons/ImageAddIcon";
import ShowIcon from "../../common/Components/Icons/ShowIcon";
import HideIcon from "../../common/Components/Icons/HideIcon";
import DownloadIcon from "../../common/Components/Icons/DownloadIcon";
import CircleLoader from "../../common/Components/Loader/CircleLoader";
import DocxIcon from "../../common/Components/Icons/DocxIcon";
import { frameClass, frameFromSize, MediaFrame } from "./mediaFrame";
import { isPreviewImage, isPreviewVideo, previewMediaUrl, shouldShowMediaUploadSpinner } from "./previewMedia";
import { retryProductMedia } from "../../uploads/productMediaUploadManager";
import { FullViewImage } from "../../media/FullViewImage";

type ProcessMediaGridProps = {
  items?: any[];
  loader?: boolean;
  onAdd?: () => void;
  onDelete?: (index: number) => void;
  onDeleteMany?: (indexes: number[]) => void | boolean | Promise<void | boolean>;
  onToggleShow?: (index: number, show: boolean) => void;
  downloadName?: string;
};

const isVideo = (item: any) => isPreviewVideo(item);
const isImage = (item: any) => isPreviewImage(item);

const isPdf = (item: any) =>
  item?.type === "application/pdf" || /\.pdf(\?|$)/i.test(previewMediaUrl(item));

const isDoc = (item: any) =>
  String(item?.type || "").includes("word") ||
  String(item?.type || "").includes("document") ||
  /\.(doc|docx)(\?|$)/i.test(previewMediaUrl(item));

export const mediaUrl = (item: any) => previewMediaUrl(item);

export const remoteMediaUrls = (items: any[], indexes: number[]) =>
  indexes
    .map((i) => String(mediaUrl(items?.[i]) || "").split("?")[0])
    .filter((url) => /^https?:\/\//i.test(url));

const holderCount = (itemCount: number, canAdd: boolean) => {
  const filled = itemCount + (canAdd ? 1 : 0);
  if (itemCount === 0) {
    return 1;
  }
  return Math.ceil(Math.max(filled, 1) / 3) * 3;
};

const PlayMark = () => (
  <span className="hkp-media-play" aria-hidden="true">
    <span className="hkp-media-play__triangle" />
  </span>
);

const ProcessMediaGrid = ({
  items,
  loader,
  onAdd,
  onDelete,
  onDeleteMany,
  onToggleShow,
  downloadName,
}: ProcessMediaGridProps) => {
  const list = (Array.isArray(items) ? items : []).filter((item) => Boolean(mediaUrl(item)));
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [videoPaused, setVideoPaused] = useState(true);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [frame, setFrame] = useState<MediaFrame>("unknown");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const userPlayedRef = useRef(false);
  const openItem = openIndex != null ? list[openIndex] : null;
  const openUrl = openItem ? mediaUrl(openItem) : "";
  const fileName = downloadName || "process";
  const canStep = list.length > 1;
  const canDelete = Boolean(onDelete || onDeleteMany);
  const listKey = list.map((item) => mediaUrl(item)).join("|");
  const allSelected = list.length > 0 && selected.size === list.length;

  const cells = useMemo(() => {
    const total = holderCount(list.length, Boolean(onAdd));
    return Array.from({ length: total }, (_, i) => i);
  }, [list.length, onAdd]);

  const closeLightbox = () => {
    userPlayedRef.current = false;
    setOpenIndex(null);
    setScale(1);
    setVideoPaused(true);
    setFrame("unknown");
  };

  const step = (delta: number) => {
    if (!canStep) return;
    userPlayedRef.current = false;
    setScale(1);
    setVideoPaused(true);
    setFrame("unknown");
    setOpenIndex((current) => {
      if (current == null) return current;
      return (current + delta + list.length) % list.length;
    });
  };

  const applyFrameFromMedia = (el: HTMLImageElement | HTMLVideoElement | null) => {
    if (!el) return;
    if (el instanceof HTMLVideoElement) {
      setFrame(frameFromSize(el.videoWidth, el.videoHeight));
      return;
    }
    setFrame(frameFromSize(el.naturalWidth, el.naturalHeight));
  };

  const playOpenVideo = () => {
    const el = videoRef.current;
    if (!el) return;
    userPlayedRef.current = true;
    el.play()
      .then(() => setVideoPaused(false))
      .catch(() => undefined);
  };

  const downloadItem = (item: any) => {
    const url = mediaUrl(item);
    if (!url) return item;
    return {
      ...item,
      blobImage: item?.blobImage || url,
      type: item?.type || (isVideo(item) ? "video/mp4" : isPdf(item) ? "application/pdf" : "image/jpeg"),
    };
  };

  const toggleSelect = (index: number) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(list.map((_, i) => i)));
  };

  const removeIndexes = async (indexes: number[]) => {
    const unique = Array.from(new Set(indexes.filter((i) => i >= 0 && i < list.length))).sort(
      (a, b) => b - a
    );
    if (!unique.length || !canDelete || deleting) return;
    const allow = window.confirm(
      unique.length === 1
        ? "Delete this photo or video from the process? It will be removed from the product."
        : `Delete ${unique.length} selected photos or videos from this process? They will be removed from the product.`
    );
    if (!allow) return;
    setDeleting(true);
    try {
      if (onDeleteMany) {
        const result = await onDeleteMany(unique);
        if (result === false) return;
      } else {
        unique.forEach((i) => onDelete?.(i));
      }
      setSelected(new Set());
      closeLightbox();
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    setSelected(new Set());
  }, [listKey]);

  useEffect(() => {
    setFrame("unknown");
  }, [openUrl]);

  useEffect(() => {
    if (openIndex == null) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        step(-1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        step(1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIndex, list.length]);

  return (
    <>
      <div className={`hkp-media-grid${list.length === 0 ? " is-empty" : ""}`} role="list">
        {cells.map((i) => {
          const item = list[i];
          const isAdd = !item && onAdd && i === list.length;
          const url = item ? mediaUrl(item) : "";
          const isChecked = selected.has(i);
          return (
            <div
              key={i}
              className={`hkp-media-grid__cell${item && isChecked ? " is-selected" : ""}`}
              role="listitem"
            >
              {shouldShowMediaUploadSpinner(Boolean(loader), item) ||
              item?.uploadStatus === "UPLOADING" ||
              item?.uploadStatus === "QUEUED" ? (
                <div className="hkp-media-grid__empty">
                  <CircleLoader />
                  <div className="hkp-media-grid__upload-label">
                    {item?.uploadStatus === "QUEUED"
                      ? "Queued"
                      : `Uploading ${Number(item?.uploadProgress || 0)}%`}
                  </div>
                </div>
              ) : item?.uploadStatus === "FAILED" ? (
                <div className="hkp-media-grid__empty">
                  <div className="hkp-media-grid__upload-label">Upload failed · Retry</div>
                  {item?.mediaId ? (
                    <button
                      type="button"
                      className="hkp-media-grid__retry"
                      onClick={() => retryProductMedia(item.mediaId)}
                    >
                      Retry
                    </button>
                  ) : null}
                </div>
              ) : item ? (
                <>
                  {canDelete ? (
                    <label
                      className="hkp-media-grid__check-wrap"
                      onClick={(event) => event.stopPropagation()}
                      onMouseDown={(event) => event.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className="hkp-media-grid__check"
                        checked={isChecked}
                        disabled={deleting}
                        onChange={() => toggleSelect(i)}
                        aria-label={`Select ${isVideo(item) ? "video" : "photo"} ${i + 1}`}
                      />
                    </label>
                  ) : null}
                  {onToggleShow ? (
                    item?.show === true || item?.show === "1" || item?.show === 1 ? (
                      <ShowIcon
                        onClick={(ev: any) => onToggleShow(i, ev.show)}
                        index={i}
                        name="flex"
                      />
                    ) : (
                      <HideIcon
                        onClick={(ev: any) => onToggleShow(i, ev.show)}
                        index={i}
                        name="flex"
                      />
                    )
                  ) : null}
                  <DownloadIcon i={i} item={downloadItem(item)} name={fileName} />
                  <button
                    type="button"
                    className="hkp-media-grid__hit"
                    onClick={() => {
                      userPlayedRef.current = false;
                      setScale(1);
                      setVideoPaused(true);
                      setFrame("unknown");
                      setOpenIndex(i);
                    }}
                    aria-label={isVideo(item) ? "Play video" : "Open media"}
                  >
                    {isVideo(item) ? (
                      <span className="hkp-media-grid__poster" />
                    ) : isImage(item) ? (
                      <img src={url} alt="" />
                    ) : isPdf(item) ? (
                      <span className="hkp-media-grid__file">PDF</span>
                    ) : isDoc(item) ? (
                      <span className="hkp-media-grid__file">
                        <DocxIcon />
                      </span>
                    ) : (
                      <img src={url} alt="" />
                    )}
                    {isVideo(item) ? <PlayMark /> : null}
                    {item?.source === "FIELD_LINK" || item?.lat != null ? (
                      <span className="hkp-media-grid__tag">Live</span>
                    ) : null}
                  </button>
                </>
              ) : isAdd ? (
                <button
                  type="button"
                  className="hkp-media-grid__empty hkp-media-grid__add"
                  onClick={onAdd}
                  aria-label="Add media"
                >
                  <span className="hkp-media-grid__add-icon">
                    <ImageAddIcon fileUploadTrigger={() => undefined} />
                  </span>
                </button>
              ) : (
                <div className="hkp-media-grid__empty" />
              )}
            </div>
          );
        })}
      </div>
      {canDelete && list.length > 0 ? (
        <div className="hkp-media-grid__toolbar">
          <label className="hkp-media-grid__toolbar-select text-muted small mb-0">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              disabled={deleting}
            />
            Select all
          </label>
          <Button
            type="button"
            size="sm"
            color="danger"
            outline
            disabled={selected.size === 0 || deleting}
            onClick={() => removeIndexes(Array.from(selected))}
          >
            {deleting
              ? "Deleting..."
              : selected.size
              ? `Delete selected (${selected.size})`
              : "Delete selected"}
          </Button>
        </div>
      ) : null}
      <Modal
        isOpen={openIndex != null}
        toggle={closeLightbox}
        fullscreen
        className="hkp-media-viewer"
        modalClassName="hkp-media-viewer"
      >
        <ModalBody>
          <div className={`hkp-media-zoom ${frameClass(frame)}`}>
            {canStep ? (
              <button
                type="button"
                className="hkp-media-zoom__nav is-prev"
                onClick={() => step(-1)}
                aria-label="Previous"
              >
                ‹
              </button>
            ) : null}
            <div className="hkp-media-zoom__stage" style={{ transform: `scale(${scale})` }}>
              {openItem && isVideo(openItem) ? (
                <>
                  <video
                    key={openUrl}
                    ref={videoRef}
                    src={openUrl}
                    controls
                    playsInline
                    preload="metadata"
                    autoPlay={false}
                    onLoadedMetadata={(event) => {
                      if (!userPlayedRef.current) event.currentTarget.pause();
                      applyFrameFromMedia(event.currentTarget);
                    }}
                    onLoadedData={(event) => {
                      if (!userPlayedRef.current) event.currentTarget.pause();
                      applyFrameFromMedia(event.currentTarget);
                    }}
                    onPlay={() => {
                      userPlayedRef.current = true;
                      setVideoPaused(false);
                    }}
                    onPause={() => setVideoPaused(true)}
                    onEnded={() => setVideoPaused(true)}
                  />
                  {videoPaused ? (
                    <button
                      type="button"
                      className="hkp-media-zoom__play"
                      onClick={playOpenVideo}
                      aria-label="Play video"
                    >
                      <PlayMark />
                    </button>
                  ) : null}
                </>
              ) : openItem && isPdf(openItem) ? (
                <iframe title="PDF" src={openUrl} />
              ) : (
                <FullViewImage
                  item={openItem}
                  src={openUrl}
                  zoomed={scale > 1}
                  onLoad={(event) => applyFrameFromMedia(event.currentTarget)}
                />
              )}
            </div>
            {canStep ? (
              <button
                type="button"
                className="hkp-media-zoom__nav is-next"
                onClick={() => step(1)}
                aria-label="Next"
              >
                ›
              </button>
            ) : null}
          </div>
          <div className="hkp-media-zoom__dock">
            {openIndex != null ? (
              <p className="hkp-media-zoom__count">
                {openIndex + 1} of {list.length}
              </p>
            ) : null}
            {openItem?.lat != null && openItem?.lng != null ? (
              <p className="hkp-media-zoom__geo">
                {Number(openItem.lat).toFixed(4)}, {Number(openItem.lng).toFixed(4)}
              </p>
            ) : null}
            <div className="hkp-media-zoom__actions">
              {canStep ? (
                <>
                  <Button type="button" size="sm" outline onClick={() => step(-1)}>
                    Previous
                  </Button>
                  <Button type="button" size="sm" outline onClick={() => step(1)}>
                    Next
                  </Button>
                </>
              ) : null}
              <Button type="button" size="sm" outline onClick={() => setScale((s) => Math.min(3, s + 0.25))}>
                Zoom in
              </Button>
              <Button type="button" size="sm" outline onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}>
                Zoom out
              </Button>
              {openItem ? <DownloadIcon i={openIndex} item={downloadItem(openItem)} name={fileName} /> : null}
              {canDelete && openIndex != null ? (
                <Button
                  type="button"
                  size="sm"
                  color="danger"
                  outline
                  disabled={deleting}
                  onClick={() => removeIndexes([openIndex])}
                >
                  Delete this
                </Button>
              ) : null}
              <Button type="button" size="sm" onClick={closeLightbox}>
                Close
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ProcessMediaGrid;
